import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { userPerms } from "@/lib/permissions";
import { I18nProvider } from "@/lib/i18n";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import AddCar from "@/pages/AddCar";
import CarDetail from "@/pages/CarDetail";
import Exports from "@/pages/Exports";
import EquipmentManager from "@/pages/EquipmentManager";
import BrandsManager from "@/pages/BrandsManager";
import UsersManager from "@/pages/UsersManager";
import SettingsPage from "@/pages/Settings";
import Presentation from "@/pages/Presentation";
import BookmarkletHelp from "@/pages/BookmarkletHelp";
import SellMyCar from "@/pages/SellMyCar";
import SellLeads from "@/pages/SellLeads";
import Trash from "@/pages/Trash";

// Stable role constants — extracted to module scope to avoid creating new array instances
// on every render (which would re-mount each <Protected> wrapper unnecessarily).
const ADMIN_PARTNER = ["admin", "partner"];
const ADMIN_ONLY = ["admin"];

const PERM_ROUTE = {
  dashboard: "/",
  inventory: "/inventory",
  add_car: "/cars/new",
  import_cargr: "/bookmarklet",
  sell_leads: "/sell-leads",
  trash: "/trash",
  exports: "/exports",
  settings: "/settings",
};

function Protected({ children, allowedRoles, perm, fallbackPath = "/" }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-sm text-[#4B5563] font-mono">Φόρτωση...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={fallbackPath} replace />;
  }
  if (perm && !userPerms(user).includes(perm)) {
    return <Navigate to={fallbackPath} replace />;
  }
  return children;
}

function PermHome() {
  // Land on the Dashboard when allowed, otherwise the first permitted page
  const { user } = useAuth();
  const perms = userPerms(user);
  if (perms.includes("dashboard")) return <Dashboard />;
  const first = ["inventory", "add_car", "sell_leads", "exports", "trash", "import_cargr", "settings"].find((p) => perms.includes(p));
  if (first) return <Navigate to={PERM_ROUTE[first]} replace />;
  return (
    <div className="border border-black/10 p-16 text-center">
      <div className="heading text-xl font-bold">Δεν έχετε πρόσβαση σε καμία ενότητα</div>
      <div className="text-sm text-[#4B5563] mt-2">Επικοινωνήστε με τον διαχειριστή.</div>
    </div>
  );
}

function App() {
  useEffect(() => {
    document.title = "BESIKOS CARS · Διαχείριση Στόλου";
  }, []);
  return (
    <div className="App">
      <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/presentation/:id" element={<Presentation />} />
            <Route path="/sell-my-car" element={<SellMyCar />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<PermHome />} />
              <Route path="/inventory" element={<Protected perm="inventory"><Inventory /></Protected>} />
              <Route path="/cars/new" element={<Protected perm="add_car"><AddCar /></Protected>} />
              <Route path="/cars/:id" element={<Protected perm="inventory"><CarDetail /></Protected>} />
              <Route path="/bookmarklet" element={<Protected perm="import_cargr"><BookmarkletHelp /></Protected>} />
              <Route path="/sell-leads" element={<Protected perm="sell_leads"><SellLeads /></Protected>} />
              <Route path="/trash" element={<Protected perm="trash"><Trash /></Protected>} />
              <Route path="/brands" element={<Protected allowedRoles={ADMIN_PARTNER}><BrandsManager /></Protected>} />
              <Route path="/equipment" element={<Protected allowedRoles={ADMIN_PARTNER}><EquipmentManager /></Protected>} />
              <Route path="/users" element={<Protected allowedRoles={ADMIN_ONLY}><UsersManager /></Protected>} />
              <Route path="/exports" element={<Protected perm="exports"><Exports /></Protected>} />
              <Route path="/settings" element={<Protected perm="settings"><SettingsPage /></Protected>} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </I18nProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
